/**
 * 🆕 [Phase 54 D.4c] Tooltip détaillé hover BT / Service Call.
 *
 * Copie 1:1 du legacy CalendarWeekRow.jsx L543-678 :
 *   - Variante Service Call : 🔧 + client + adresse + description + employés assignés
 *   - Variante BT régulier  : dossier + chef + type + heures + équipements
 *                             + punches actifs (durée) + punches terminés + actions
 *
 * Implémentation : `<div>` absolu (pas Radix) positionné `top-full mt-1`
 * relativement au parent qui DOIT avoir `position: relative`.
 *
 * Activation : conditionnel sur `hoveredBonId === bon.id` (state local côté
 * GanttGridLane via onMouseEnter/Leave).
 */
import { User, ExternalLink, CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';

function GanttBonTooltip({
  bon, dossier,
  activePunches = [],
  completedPunches = [],
  navigate, openEditDatesDialog,
  // 🆕 [2026-06-11] handlers fournis par GanttGridCalendrier — survol du
  // tooltip annule la fermeture (cf. fix UX « trop sensible »).
  onMouseEnter,
  onMouseLeave,
}) {
  const isServiceCall = bon.isServiceCall;
  const clientNom = bon.client_nom || dossier?.client_nom || '';

  // Variante Service Call (orange border)
  if (isServiceCall) {
    return (
      <div
        className="absolute z-50 left-0 top-full pt-1 bg-transparent"
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        data-testid={`gantt-tooltip-sc-${bon.id}`}
      >
        <div className="bg-white border-2 border-orange-200 rounded-lg shadow-xl p-3 min-w-[280px] text-gray-800">
        <p className="font-semibold text-sm mb-1 flex items-center gap-1.5">
          <span className="text-orange-600">🔧</span> Service Call
        </p>
        <p className="text-sm font-medium text-slate-800">{clientNom || '(sans client)'}</p>
        {bon.nom_dossier && bon.nom_dossier !== clientNom && (
          <p className="text-xs text-gray-500">{bon.nom_dossier}</p>
        )}
        {bon.adresse_travaux && (
          <p className="text-xs text-gray-500 mt-0.5">📍 {bon.adresse_travaux}</p>
        )}
        {bon.description && (
          <p className="text-xs text-gray-600 mt-1.5 italic">{bon.description}</p>
        )}
        <div className="mt-2 pt-2 border-t">
          <p className="text-xs font-semibold text-slate-700 mb-1">Employés assignés :</p>
          {(bon.employes || []).length === 0 ? (
            <p className="text-xs text-gray-400 italic">Aucun employé assigné</p>
          ) : (
            <ul
              className="text-xs text-slate-700 space-y-0.5"
              data-testid={`sc-employes-tooltip-${bon.id}`}
            >
              {(bon.employes || []).map(e => (
                <li key={e.id} className="flex items-center gap-1">
                  <User className="w-3 h-3 text-orange-500" />
                  {e.nom_complet || e.nom || e.prenom || e.id}
                </li>
              ))}
            </ul>
          )}
          {bon.chef_equipe_nom && (
            <p className="text-[11px] text-gray-500 mt-1.5">
              👤 Chef : <span className="font-medium">{bon.chef_equipe_nom}</span>
            </p>
          )}
        </div>
        <p className="text-[11px] text-gray-400 italic mt-2">
          Cliquez pour pointer ou voir les détails
        </p>
        </div>
      </div>
    );
  }

  // Variante BT régulier
  return (
    <div
      className="absolute z-50 left-0 top-full pt-1 bg-transparent"
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-testid={`gantt-tooltip-bt-${bon.id}`}
    >
      <div className="bg-white border rounded-lg shadow-xl p-3 min-w-[280px] text-gray-800">
      <p className="font-semibold text-sm mb-1">{dossier?.nom_dossier || 'Chargement...'}</p>
      <p className="text-xs text-gray-500 mb-2">Client: {dossier?.client_nom || '...'}</p>
      <div className="flex items-center gap-1 mb-1">
        <span className="w-3 h-3 rounded-full" style={{ backgroundColor: bon.color }} />
        <span className="text-xs font-medium">{bon.chef_equipe_nom || 'Aucun chef'}</span>
      </div>
      <div className="text-xs text-gray-500">
        <p>
          {bon.type_travaux === 'interieur' ? '☁️ Intérieur' : '☀️ Extérieur'}
          {' — '}
          {bon.heures_budgetees || 0}h budgétées
        </p>
        <p>{(bon.dates_chantier || []).length} jours planifiés</p>
      </div>

      {/* Équipements — exclure nacelle (ancien format) et afficher nacelle via equipements_nacelle_* */}
      {(() => {
        const equips = (bon.equipements || []).filter(eq =>
          !((eq.equipement_nom || '').toLowerCase().includes('nacelle'))
        );
        const nacelleDays = (bon.equipements_nacelle_dates || []).length;
        const showEquips = equips.length > 0 || (bon.equipements_nacelle_requise && nacelleDays > 0);
        if (!showEquips) return null;
        return (
          <div className="mt-1">
            <p className="text-xs text-gray-500 font-medium">Équipements :</p>
            {bon.equipements_nacelle_requise && nacelleDays > 0 && (
              <p className="text-xs">🏗️ Nacelle ({nacelleDays} jours)</p>
            )}
            {equips.map((eq, i) => (
              <p
                key={eq.id || eq._id || i}
                className="text-xs"
              >
                {eq.emoji} {eq.equipement_nom} ({eq.dates_utilisation?.length || 0} jours)
              </p>
            ))}
          </div>
        );
      })()}

      {/* Punches : actifs + terminés */}
      <div className="mt-2 pt-2 border-t">
        {activePunches.length > 0 ? (
          <div
            className="bg-green-50 border border-green-200 rounded-lg p-2 mb-2"
            data-testid={`gantt-tooltip-active-punches-${bon.id}`}
          >
            <p className="text-xs font-semibold text-green-800 mb-1 flex items-center gap-1">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              Employés sur le chantier ({activePunches.length})
            </p>
            {activePunches.map((p, i) => {
              const punchTime = p.punch_in_time ? new Date(p.punch_in_time) : null;
              const now = new Date();
              const diffMs = punchTime ? now - punchTime : 0;
              const heures = Math.floor(diffMs / 3600000);
              const mins = Math.floor((diffMs % 3600000) / 60000);
              const duree = heures > 0
                ? `${heures}h${mins > 0 ? String(mins).padStart(2, '0') : ''}`
                : `${mins} min`;
              return (
                <div key={i} className="flex items-center justify-between text-xs py-0.5">
                  <span className="text-green-700 font-medium">{p.employee_name}</span>
                  <span className="text-green-600 font-semibold">depuis {duree}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-xs text-gray-400 mb-2">Aucun employé sur le chantier</p>
        )}

        {completedPunches.length > 0 && (
          <div data-testid={`gantt-tooltip-completed-punches-${bon.id}`}>
            <p className="text-xs text-gray-500 font-medium mb-1">Pointages terminés :</p>
            {completedPunches.slice(0, 5).map((p, i) => (
              <div
                key={p.id || p._id || i}
                className="text-xs text-gray-500"
              >
                {p.employee_name} — {p.heures_nettes?.toFixed(2).replace('.', ',') || '?'}h
              </div>
            ))}
            {completedPunches.length > 5 && (
              <p className="text-xs text-gray-400">+{completedPunches.length - 5} autres...</p>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="mt-3 pt-2 border-t flex gap-2">
        <Button
          size="sm"
          className="flex-1 text-xs bg-blue-600 hover:bg-blue-700"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/crm/projets/${bon.dossier_id}?tab=bon-travail&bonId=${bon.id}`);
          }}
          data-testid={`gantt-tooltip-voir-bon-${bon.id}`}
        >
          <ExternalLink className="w-3 h-3 mr-1" />
          Voir le bon
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="flex-1 text-xs"
          onClick={(e) => {
            e.stopPropagation();
            openEditDatesDialog(bon);
          }}
          data-testid={`gantt-tooltip-edit-dates-${bon.id}`}
        >
          <CalendarIcon className="w-3 h-3 mr-1" />
          Modifier dates
        </Button>
      </div>
      </div>
    </div>
  );
}

export default GanttBonTooltip;
